import { useAppUser } from '@bechtel/client-common';
import {
  CommodityComplexFilterInput,
  CommodityFilterByTerm,
  CommodityStatusCode,
  FilterOperator,
  PersonaType,
} from '@bechtel/types';
import { find } from 'lodash';
import { useEffect } from 'react';
import { useWeeklyCommodityStatsQuery } from '../use-weekly-commodity-stats';

export function useWeeklyCommodityQueryResult(weldTypeId, crewMember, weldFilter) {
  const { user } = useAppUser();
  const [
    getWeeklyCommodityStats,
    {
      error: weeklyCommodityStatsError,
      data: weeklyCommodityStatsData,
      loading: weeklyCommodityStatsLoading,
      refetch: weeklyRefetch,
    },
  ] = useWeeklyCommodityStatsQuery(true);
  useEffect(() => {
    if (
      !crewMember?.persona ||
      !crewMember?.id ||
      !user ||
      !weldTypeId ||
      crewMember?.persona === PersonaType.SUPER_ADMIN
    ) {
      return;
    }
    const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
      projectWeek?.isCurrent ? true : false
    );
    const complexFilterInput: CommodityComplexFilterInput = {
      and: [
        {
          term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
          value: weldTypeId,
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.PRIMARY_CREW_MEMBER_ID,
          value: crewMember.id,
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.START_DATE,
          value: currentProjectWeek?.startDate,
          operator: FilterOperator.GREATER_THAN,
        },
        {
          term: CommodityFilterByTerm.END_DATE,
          value: currentProjectWeek?.endDate,
          operator: FilterOperator.LESS_THAN,
        },
        {
          term: CommodityFilterByTerm.ASSIGNED_CREW_MEMBER,
          value: 'true',
          operator: FilterOperator.EQUALS,
        },
      ],
    };
    if (crewMember?.persona && crewMember.persona === PersonaType.FIELD_WELD_ENGINEER) {
      complexFilterInput.and?.push(
        {
          term: CommodityFilterByTerm.PERSONA_TYPE_ID,
          value: crewMember?.persona,
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.APPLY_ASSOCIATED_CREW_MEMBERS,
          value: 'true',
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
          value: CommodityStatusCode.VT_APPROVED,
          operator: FilterOperator.EQUALS,
        }
      );

      if (weldFilter?.searchByForeman?.length > 0) {
        complexFilterInput.and?.push({
          term: CommodityFilterByTerm.CREW_MEMBER_ID,
          values: [...weldFilter.searchByForeman],
          operator: FilterOperator.IN,
        });
      }
    } else {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
        values: [CommodityStatusCode.CLAIMED, CommodityStatusCode.VT_APPROVED],
        operator: FilterOperator.IN,
      });
    }
    getWeeklyCommodityStats({
      variables: {
        input: {
          complexFilter: complexFilterInput,
        },
      },
    });
  }, [crewMember?.persona, crewMember?.id, getWeeklyCommodityStats, user, weldTypeId, weldFilter]);
  return {
    weeklyCommodityStatsError,
    weeklyCommodityStatsData,
    weeklyCommodityStatsLoading,
    weeklyRefetch,
  };
}
